// modal.js - Custom modal dialogs

/**
 * Show a confirmation modal
 * @param {string} message - The message to display
 * @param {string} title - The modal title (optional)
 * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
 */
export function showConfirmation(message, title = 'Bestätigung') {
  return new Promise((resolve) => {
    const modal = document.getElementById('confirmation-modal');
    const modalTitle = document.getElementById('modal-title');
    const modalMessage = document.getElementById('modal-message');
    const confirmBtn = document.getElementById('modal-confirm');
    const cancelBtn = document.getElementById('modal-cancel');

    if (!modal || !modalTitle || !modalMessage || !confirmBtn || !cancelBtn) {
      console.error('Modal elements not found');
      resolve(false);
      return;
    }

    // Set content
    modalTitle.textContent = title;
    modalMessage.textContent = message;

    // Show modal
    modal.style.display = 'flex';

    // Handle confirm
    const handleConfirm = () => {
      cleanup();
      resolve(true);
    };

    // Handle cancel
    const handleCancel = () => {
      cleanup();
      resolve(false);
    };

    // Handle overlay click (close on background click)
    const handleOverlayClick = (e) => {
      if (e.target === modal) {
        handleCancel();
      }
    };

    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    // Cleanup function
    const cleanup = () => {
      modal.style.display = 'none';
      confirmBtn.removeEventListener('click', handleConfirm);
      cancelBtn.removeEventListener('click', handleCancel);
      modal.removeEventListener('click', handleOverlayClick);
      document.removeEventListener('keydown', handleEscape);
    };

    // Add event listeners
    confirmBtn.addEventListener('click', handleConfirm);
    cancelBtn.addEventListener('click', handleCancel);
    modal.addEventListener('click', handleOverlayClick);
    document.addEventListener('keydown', handleEscape);

    // Focus on confirm button
    setTimeout(() => confirmBtn.focus(), 100);
  });
}

/**
 * Show an alert modal
 * @param {string} message - The message to display
 * @param {string} title - The modal title (optional)
 * @returns {Promise<void>}
 */
export function showAlert(message, title = 'Hinweis') {
  return new Promise((resolve) => {
    const modal = document.getElementById('confirmation-modal');
    const modalTitle = document.getElementById('modal-title');
    const modalMessage = document.getElementById('modal-message');
    const confirmBtn = document.getElementById('modal-confirm');
    const cancelBtn = document.getElementById('modal-cancel');

    if (!modal || !modalTitle || !modalMessage || !confirmBtn || !cancelBtn) {
      console.error('Modal elements not found');
      resolve();
      return;
    }

    // Set content
    modalTitle.textContent = title;
    modalMessage.textContent = message;

    // Hide cancel button for alerts
    cancelBtn.style.display = 'none';

    // Show modal
    modal.style.display = 'flex';

    // Handle confirm
    const handleConfirm = () => {
      cleanup();
      resolve();
    };

    // Handle overlay click
    const handleOverlayClick = (e) => {
      if (e.target === modal) {
        handleConfirm();
      }
    };

    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleConfirm();
      }
    };

    // Cleanup function
    const cleanup = () => {
      modal.style.display = 'none';
      cancelBtn.style.display = ''; // Restore cancel button
      confirmBtn.removeEventListener('click', handleConfirm);
      modal.removeEventListener('click', handleOverlayClick);
      document.removeEventListener('keydown', handleEscape);
    };

    // Add event listeners
    confirmBtn.addEventListener('click', handleConfirm);
    modal.addEventListener('click', handleOverlayClick);
    document.addEventListener('keydown', handleEscape);

    // Focus on confirm button
    setTimeout(() => confirmBtn.focus(), 100);
  });
}
